import React, { useState, useEffect } from 'react';
import CategoryCount from './CategoryCount';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { utils } from 'xlsx';
import { saveAs } from 'file-saver';

const StatsTable = () => {
  const { date } = useParams();
  const [data, setData] = useState([]);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  // Función para calcular los gastos totales
  const calcularGastosTotales = () => {
    const gastos = {};
    data.forEach((pedido) => {
      if (pedido.gastosData && pedido.gastosData[0]) {
        pedido.gastosData[0].forEach((gasto) => {
          if (gasto.name in gastos) {
            gastos[gasto.name] += gasto.price;
          } else {
            gastos[gasto.name] = gasto.price;
          }
        });
      }
    });
    return gastos;
  };

  const gastosTotales = calcularGastosTotales();

  // Función para calcular totales correctamente
  const calcularTotales = () => {
    let ingresoTotal = 0;
    let gastoTotal = 0;
    let domiciliosTotal = 0;
    let dineroDomiciliosTotal = 0;

    data.forEach((pedido) => {
      ingresoTotal += pedido.total || 0;
      gastoTotal += pedido.totalSalida || 0;
      domiciliosTotal += pedido.numeroDomicilios || 0;
      dineroDomiciliosTotal += pedido.dineroDomicilios || 0;
    });

    return {
      ingresoTotal,
      gastoTotal,
      balanceTotal: ingresoTotal - gastoTotal,
      domiciliosTotal,
      dineroDomiciliosTotal
    };
  };

  const totales = calcularTotales();

  // Método de ordenamiento mejorado
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      if (key === 'balance') {
        const balanceA = a.total - a.totalSalida;
        const balanceB = b.total - b.totalSalida;
        return direction === 'ascending' ? balanceA - balanceB : balanceB - balanceA;
      }

      if (typeof a[key] === 'number' && typeof b[key] === 'number') {
        return direction === 'ascending' ? a[key] - b[key] : b[key] - a[key];
      }

      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });

    setFilteredData(sortedData);
  };

  // Formatear números para mostrar con separadores de miles
  const formatNumber = (num) => {
    return num ? num.toLocaleString('es-CO') : '0';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validar el formato del precio
    const priceValue = price.replace(/\./g, '').replace(',', '.');
    if (isNaN(parseFloat(priceValue))) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Por favor ingrese un valor numérico válido',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    setLoading(true);
    axios.post(`${import.meta.env.VITE_IP}/addGasto`, { 
      name, 
      price: parseFloat(priceValue) 
    })
      .then((response) => {
        Swal.fire({
          icon: 'success',
          title: 'Gasto agregado',
          text: `Se ha agregado el gasto "${name}" correctamente`,
          confirmButtonColor: '#28a745'
        });
        setName('');
        setPrice('');
        // Recargar datos
        axios.post(`${import.meta.env.VITE_IP}/getAll${date}`)
          .then(json => {
            setData(json.data);
            setFilteredData(json.data);
            setLoading(false);
          });
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ha ocurrido un error al agregar el gasto',
          confirmButtonColor: '#dc3545'
        });
        setLoading(false);
      });
  };

  // Función para formatear el precio al escribir
  const handlePriceChange = (e) => {
    // Eliminar cualquier carácter que no sea un número
    const value = e.target.value.replace(/[^\d]/g, '');
    // Formatear con separadores de miles
    if (value) {
      const formattedValue = new Intl.NumberFormat('es-CO').format(parseInt(value));
      setPrice(formattedValue);
    } else {
      setPrice('');
    }
  };

  // Función mejorada para exportar a Excel
  const exportToExcel = async () => {
    try {
      // Create a new workbook
      const workbook = new ExcelJS.Workbook();
      
      
      // Set workbook properties
      workbook.creator = 'Heladería POS System';
      workbook.lastModifiedBy = 'Heladería POS System';
      workbook.created = new Date();
      workbook.modified = new Date();
      
      // Determine report type for title
      const reportType = date === "day" ? "Diario" : date === "month" ? "Mensual" : "Anual";
      const currentDate = new Date().toLocaleDateString('es-CO').replace(/\//g, '-');
      
      // ===== RESUMEN SHEET =====
      const resumenSheet = workbook.addWorksheet('Resumen', {
        properties: { tabColor: { argb: '6495ED' } }
      });
      
      // Add logo to the summary sheet if available

      
      // Set column widths
      resumenSheet.columns = [
        { header: 'Concepto', key: 'concepto', width: 30 },
        { header: 'Valor', key: 'valor', width: 20 },
        { header: 'Porcentaje', key: 'porcentaje', width: 20 },
        { header: 'Observaciones', key: 'observaciones', width: 40 }
      ];
      
      // Add title with merged cells
      resumenSheet.mergeCells('A1:D3');
      const titleCell = resumenSheet.getCell('A1');
      titleCell.value = `REPORTE ${reportType.toUpperCase()} - ${currentDate}`;
      titleCell.font = {
        name: 'Arial',
        size: 16,
        bold: true,
        color: { argb: '4F4F4F' }
      };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      
      // Style header row
      resumenSheet.getRow(4).font = { bold: true, size: 12 };
      resumenSheet.getRow(4).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '6495ED' }
      };
      resumenSheet.getRow(4).alignment = { vertical: 'middle', horizontal: 'center' };
      resumenSheet.getRow(4).font = { bold: true, color: { argb: 'FFFFFF' } };
      
      // Add summary data
      const resumenData = [
        { 
          concepto: 'Total de Ingresos', 
          valor: totales.ingresoTotal, 
          porcentaje: '100%',
          observaciones: 'Suma total de todos los ingresos en el período'
        },
        { 
          concepto: 'Total de Gastos', 
          valor: totales.gastoTotal, 
          porcentaje: `${((totales.gastoTotal / totales.ingresoTotal) * 100).toFixed(2)}%`,
          observaciones: 'Suma total de todos los gastos en el período'
        },
        { 
          concepto: 'Balance Neto', 
          valor: totales.balanceTotal, 
          porcentaje: `${((totales.balanceTotal / totales.ingresoTotal) * 100).toFixed(2)}%`,
          observaciones: 'Diferencia entre ingresos y gastos'
        },
        { 
          concepto: 'Total de Domicilios', 
          valor: totales.domiciliosTotal, 
          porcentaje: '-',
          observaciones: 'Cantidad total de domicilios realizados'
        },
        { 
          concepto: 'Ingresos por Domicilios', 
          valor: totales.dineroDomiciliosTotal, 
          porcentaje: `${((totales.dineroDomiciliosTotal / totales.ingresoTotal) * 100).toFixed(2)}%`,
          observaciones: 'Ingresos generados por domicilios'
        }
      ];
      
      // Add rows starting from row 5 (after header)
      resumenData.forEach((item, index) => {
        const rowIndex = index + 5;
        resumenSheet.addRow([item.concepto, item.valor, item.porcentaje, item.observaciones]);
        
        // Format currency cells
        resumenSheet.getCell(`B${rowIndex}`).numFmt = '$#,##0';
        
        // Alternate row colors
        if (index % 2 === 0) {
          resumenSheet.getRow(rowIndex).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F5F5F5' }
          };
        }
      });
      
      // Note: Removed chart code that was causing errors
      // Instead, add a note about distribution
      resumenSheet.addRow([]);
      resumenSheet.addRow(['Nota: Distribución de Ingresos vs Gastos']);
      resumenSheet.addRow([`Ingresos Netos: ${((totales.balanceTotal / (totales.balanceTotal + totales.gastoTotal)) * 100).toFixed(2)}%`]);
      resumenSheet.addRow([`Gastos: ${((totales.gastoTotal / (totales.balanceTotal + totales.gastoTotal)) * 100).toFixed(2)}%`]);
      
      // ===== DATOS PRINCIPALES SHEET =====
      const mainSheet = workbook.addWorksheet('Datos Principales', {
        properties: { tabColor: { argb: '4CAF50' } }
      });
      
      // Set column headers based on date type
      const periodLabel = date === "day" ? "Día" : date === "month" ? "Mes" : "Año";
      
      // Configure columns
      const mainColumns = [
        { header: periodLabel, key: 'periodo', width: 20 },
        { header: 'Ingresos', key: 'ingresos', width: 15 },
        { header: 'Gastos', key: 'gastos', width: 15 },
        { header: 'Balance', key: 'balance', width: 15 },
        { header: 'Domicilios (Cantidad)', key: 'domicilios', width: 20 }
      ];
      
      // Add domicilios income column for daily reports
      if (date === "day") {
        mainColumns.push({ header: 'Ingresos por Domicilios', key: 'dineroDomicilios', width: 25 });
      }
      
      mainSheet.columns = mainColumns;
      
      // Style header row
      mainSheet.getRow(1).font = { bold: true, size: 12 };
      mainSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4CAF50' }
      };
      mainSheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      mainSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      
      // Add data rows
      data.forEach((pedido, index) => {
        const row = {
          periodo: pedido.data,
          ingresos: pedido.total || 0,
          gastos: pedido.totalSalida || 0,
          balance: (pedido.total || 0) - (pedido.totalSalida || 0),
          domicilios: pedido.numeroDomicilios || 0
        };
        
        if (date === "day") {
          row.dineroDomicilios = pedido.dineroDomicilios || 0;
        }
        
        mainSheet.addRow(row);
        
        // Format currency cells
        mainSheet.getCell(`B${index + 2}`).numFmt = '$#,##0';
        mainSheet.getCell(`C${index + 2}`).numFmt = '$#,##0';
        mainSheet.getCell(`D${index + 2}`).numFmt = '$#,##0';
        
        if (date === "day") {
          mainSheet.getCell(`F${index + 2}`).numFmt = '$#,##0';
        }
        
        // Color balance cells based on value
        const balanceCell = mainSheet.getCell(`D${index + 2}`);
        if (row.balance < 0) {
          balanceCell.font = { color: { argb: 'FF0000' } };
        } else {
          balanceCell.font = { color: { argb: '008000' } };
        }
        
        // Alternate row colors
        if (index % 2 === 1) {
          mainSheet.getRow(index + 2).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F5F5F5' }
          };
        }
      });
      
      // Add totals row
      const totalRowIndex = data.length + 2;
      const totalRow = mainSheet.addRow({
        periodo: 'TOTAL',
        ingresos: totales.ingresoTotal,
        gastos: totales.gastoTotal,
        balance: totales.balanceTotal,
        domicilios: totales.domiciliosTotal
      });
      
      if (date === "day") {
        totalRow.getCell(6).value = totales.dineroDomiciliosTotal;
        totalRow.getCell(6).numFmt = '$#,##0';
      }
      
      // Style totals row
      totalRow.font = { bold: true };
      totalRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DDDDDD' }
      };
      
      // Format currency cells in totals row
      totalRow.getCell(2).numFmt = '$#,##0';
      totalRow.getCell(3).numFmt = '$#,##0';
      totalRow.getCell(4).numFmt = '$#,##0';
      
      // Note: Removed chart code that was causing errors
      // Instead, add a note about evolution
      mainSheet.addRow([]);
      mainSheet.addRow([`Nota: La evolución de Ingresos y Gastos por ${periodLabel} se puede visualizar en los datos anteriores.`]);
      
      // ===== PRODUCTOS SHEET =====
      const productosSheet = workbook.addWorksheet('Productos', {
        properties: { tabColor: { argb: 'FF9800' } }
      });
      
      // Set columns
      productosSheet.columns = [
        { header: 'Producto', key: 'producto', width: 30 },
        { header: 'Cantidad Vendida', key: 'cantidad', width: 20 },
        { header: 'Ingresos Generados', key: 'ingresos', width: 20 },
        { header: 'Precio Promedio', key: 'promedio', width: 20 },
        { header: '% del Total', key: 'porcentaje', width: 15 }
      ];
      
      // Style header row
      productosSheet.getRow(1).font = { bold: true, size: 12 };
      productosSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF9800' }
      };
      productosSheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      productosSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      
      // Prepare products data
      const productosData = [];
      data.forEach(pedido => {
        if (pedido.ingresosData?.cantidadProducto) {
          Object.entries(pedido.ingresosData.cantidadProducto).forEach(([producto, cantidad]) => {
            const dinero = pedido.ingresosData.dineroProducto[producto] || 0;
            
            // Find if product already exists in array
            const existingIndex = productosData.findIndex(item => item.producto === producto);
            
            if (existingIndex >= 0) {
              // Update values if product exists
              productosData[existingIndex].cantidad += cantidad;
              productosData[existingIndex].ingresos += dinero;
            } else {
              // Add new product if it doesn't exist
              productosData.push({
                producto,
                cantidad,
                ingresos: dinero,
                promedio: dinero / cantidad,
                porcentaje: 0 // Will calculate after all products are processed
              });
            }
          });
        }
      });
      
      // Calculate percentages and sort by revenue
      const totalIngresos = productosData.reduce((sum, item) => sum + item.ingresos, 0);
      productosData.forEach(item => {
        item.porcentaje = (item.ingresos / totalIngresos) * 100;
      });
      
      // Sort products by revenue (highest to lowest)
      productosData.sort((a, b) => b.ingresos - a.ingresos);
      
      // Add product data rows
      productosData.forEach((item, index) => {
        productosSheet.addRow({
          producto: item.producto,
          cantidad: item.cantidad,
          ingresos: item.ingresos,
          promedio: item.promedio,
          porcentaje: item.porcentaje
        });
        
        const rowIndex = index + 2;
        
        // Format cells
        productosSheet.getCell(`C${rowIndex}`).numFmt = '$#,##0';
        productosSheet.getCell(`D${rowIndex}`).numFmt = '$#,##0';
        productosSheet.getCell(`E${rowIndex}`).numFmt = '0.00%';
        
        // Alternate row colors
        if (index % 2 === 1) {
          productosSheet.getRow(rowIndex).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F5F5F5' }
          };
        }
      });
      
      // Add totals row
      const productosTotalRow = productosSheet.addRow({
        producto: 'TOTAL',
        cantidad: productosData.reduce((sum, item) => sum + item.cantidad, 0),
        ingresos: totalIngresos,
        promedio: totalIngresos / productosData.reduce((sum, item) => sum + item.cantidad, 0),
        porcentaje: 100
      });
      
      // Style totals row
      productosTotalRow.font = { bold: true };
      productosTotalRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DDDDDD' }
      };
      
      // Format currency cells in totals row
      productosTotalRow.getCell(3).numFmt = '$#,##0';
      productosTotalRow.getCell(4).numFmt = '$#,##0';
      productosTotalRow.getCell(5).numFmt = '0.00%';
      
      // Note: Removed chart code that was causing errors
      // Instead, add a table with top 5 products
      productosSheet.addRow([]);
      productosSheet.addRow(['Top 5 Productos por Ingresos']);
      
      // Create a table for top 5 products
      const top5Products = productosData.slice(0, 5);
      top5Products.forEach((product, index) => {
        productosSheet.addRow([
          `${index + 1}. ${product.producto}`, 
          '', 
          `$${product.ingresos.toLocaleString('es-CO')}`, 
          '', 
          `${product.porcentaje.toFixed(2)}%`
        ]);
      });
      
      // ===== GASTOS SHEET =====
      const gastosSheet = workbook.addWorksheet('Gastos', {
        properties: { tabColor: { argb: 'F44336' } }
      });
      
      // Set columns
      gastosSheet.columns = [
        { header: 'Concepto', key: 'concepto', width: 30 },
        { header: 'Monto', key: 'monto', width: 20 },
        { header: '% del Total de Gastos', key: 'porcentajeGastos', width: 25 },
        { header: '% de Ingresos', key: 'porcentajeIngresos', width: 20 }
      ];
      
      // Style header row
      gastosSheet.getRow(1).font = { bold: true, size: 12 };
      gastosSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F44336' }
      };
      gastosSheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      gastosSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      
      // Prepare and sort gastos data
      const gastosData = Object.keys(gastosTotales).map(nombre => ({
        concepto: nombre,
        monto: gastosTotales[nombre],
        porcentajeGastos: (gastosTotales[nombre] / totales.gastoTotal) * 100,
        porcentajeIngresos: (gastosTotales[nombre] / totales.ingresoTotal) * 100
      }));
      
      // Sort by amount (highest to lowest)
      gastosData.sort((a, b) => b.monto - a.monto);
      
      // Add gastos data rows
      gastosData.forEach((item, index) => {
        gastosSheet.addRow(item);
        
        const rowIndex = index + 2;
        
        // Format cells
        gastosSheet.getCell(`B${rowIndex}`).numFmt = '$#,##0';
        gastosSheet.getCell(`C${rowIndex}`).numFmt = '0.00%';
        gastosSheet.getCell(`D${rowIndex}`).numFmt = '0.00%';
        
        // Alternate row colors
        if (index % 2 === 1) {
          gastosSheet.getRow(rowIndex).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F5F5F5' }
          };
        }
      });
      
      // Add totals row
      const gastosTotalRow = gastosSheet.addRow({
        concepto: 'TOTAL',
        monto: totales.gastoTotal,
        porcentajeGastos: 100,
        porcentajeIngresos: (totales.gastoTotal / totales.ingresoTotal) * 100
      });
      
      // Style totals row
      gastosTotalRow.font = { bold: true };
      gastosTotalRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DDDDDD' }
      };
      
      // Format currency cells in totals row
      gastosTotalRow.getCell(2).numFmt = '$#,##0';
      gastosTotalRow.getCell(3).numFmt = '0.00%';
      gastosTotalRow.getCell(4).numFmt = '0.00%';
      
      // Note: Removed chart code that was causing errors
      // Instead, add a table with top 5 expenses
      gastosSheet.addRow([]);
      gastosSheet.addRow(['Top 5 Gastos por Monto']);
      
      // Create a table for top 5 expenses
      const top5Gastos = gastosData.slice(0, 5);
      top5Gastos.forEach((gasto, index) => {
        gastosSheet.addRow([
          `${index + 1}. ${gasto.concepto}`, 
          `$${gasto.monto.toLocaleString('es-CO')}`, 
          `${gasto.porcentajeGastos.toFixed(2)}%`, 
          `${gasto.porcentajeIngresos.toFixed(2)}%`
        ]);
      });
      
      // ===== INDICADORES SHEET =====
      const indicadoresSheet = workbook.addWorksheet('Indicadores', {
        properties: { tabColor: { argb: '9C27B0' } }
      });
      
      // Set columns
      indicadoresSheet.columns = [
        { header: 'Indicador', key: 'indicador', width: 40 },
        { header: 'Valor', key: 'valor', width: 20 },
        { header: 'Interpretación', key: 'interpretacion', width: 50 }
      ];
      
      // Style header row
      indicadoresSheet.getRow(1).font = { bold: true, size: 12 };
      indicadoresSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '9C27B0' }
      };
      indicadoresSheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      indicadoresSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      
      // Calculate indicators
      const margenBeneficio = totales.balanceTotal / totales.ingresoTotal * 100;
      const ratioGastoIngreso = totales.gastoTotal / totales.ingresoTotal * 100;
      const ingresoPorDomicilio = totales.domiciliosTotal > 0 ? 
                                 totales.dineroDomiciliosTotal / totales.domiciliosTotal : 0;
      const porcentajeDomicilios = totales.ingresoTotal > 0 ? 
                                  totales.dineroDomiciliosTotal / totales.ingresoTotal * 100 : 0;
      
      // Add indicators data
      const indicadoresData = [
        {
          indicador: 'Margen de Beneficio',
          valor: margenBeneficio,
          interpretacion: margenBeneficio >= 30 ? 
                         'Excelente: El negocio tiene un margen de beneficio saludable.' :
                         margenBeneficio >= 15 ? 
                         'Bueno: El margen es aceptable pero podría mejorarse.' :
                         'Atención: El margen de beneficio es bajo, revisar costos.'
        },
        {
          indicador: 'Ratio Gasto/Ingreso',
          valor: ratioGastoIngreso,
          interpretacion: ratioGastoIngreso <= 70 ?
                         'Excelente: Los gastos están bien controlados.' :
                         ratioGastoIngreso <= 85 ?
                         'Aceptable: Los gastos son manejables pero vigilar incrementos.' :
                         'Atención: Los gastos son elevados en relación a los ingresos.'
        },
        {
          indicador: 'Ingreso Promedio por Domicilio',
          valor: ingresoPorDomicilio,
          interpretacion: 'Valor promedio de cada servicio a domicilio realizado.'
        },
        {
          indicador: 'Porcentaje de Ingresos por Domicilios',
          valor: porcentajeDomicilios,
          interpretacion: porcentajeDomicilios >= 30 ?
                         'Los domicilios son una parte importante del negocio.' :
                         'Los domicilios representan una parte menor de los ingresos.'
        },
        {
          indicador: 'Producto Más Vendido',
          valor: productosData.length > 0 ? productosData[0].producto : 'N/A',
          interpretacion: 'Producto estrella que genera más ingresos.'
        },
        {
          indicador: 'Gasto Principal',
          valor: gastosData.length > 0 ? gastosData[0].concepto : 'N/A',
          interpretacion: 'Concepto que representa el mayor gasto.'
        }
      ];
      
      // Add indicator rows
      indicadoresData.forEach((item, index) => {
        indicadoresSheet.addRow(item);
        
        const rowIndex = index + 2;
        
        // Format percentage cells
        if (index < 4) {
          if (index !== 2) {
            indicadoresSheet.getCell(`B${rowIndex}`).numFmt = '0.00%';
          } else {
            indicadoresSheet.getCell(`B${rowIndex}`).numFmt = '$#,##0';
          }
        }
        
        // Color code indicators
        if (index === 0) { // Margen de Beneficio
          const cell = indicadoresSheet.getCell(`B${rowIndex}`);
          if (margenBeneficio >= 30) {
            cell.font = { color: { argb: '008000' } }; // Green
          } else if (margenBeneficio >= 15) {
            cell.font = { color: { argb: 'FFA500' } }; // Orange
          } else {
            cell.font = { color: { argb: 'FF0000' } }; // Red
          }
        }
        
        if (index === 1) { // Ratio Gasto/Ingreso
          const cell = indicadoresSheet.getCell(`B${rowIndex}`);
          if (ratioGastoIngreso <= 70) {
            cell.font = { color: { argb: '008000' } }; // Green
          } else if (ratioGastoIngreso <= 85) {
            cell.font = { color: { argb: 'FFA500' } }; // Orange
          } else {
            cell.font = { color: { argb: 'FF0000' } }; // Red
          }
        }
        
        // Alternate row colors
        if (index % 2 === 1) {
          indicadoresSheet.getRow(rowIndex).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F5F5F5' }
          };
        }
      });
      
      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Name the file according to report type
      const fileName = `Reporte_${reportType}_${currentDate}.xlsx`;
      
      saveAs(blob, fileName);
      
      Swal.fire({
        icon: 'success',
        title: 'Excel Generado',
        text: `Se ha generado el archivo ${fileName} correctamente`,
        confirmButtonColor: '#28a745'
      });
    } catch (error) {
      console.error('Error generating Excel:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al generar el archivo Excel',
        confirmButtonColor: '#dc3545'
      });
    }
  };
  
  // Función para filtrar datos
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(pedido => 
        pedido.data.toLowerCase().includes(term) ||
        (Object.keys(pedido.ingresosData?.cantidadProducto || {}).some(
          producto => producto.toLowerCase().includes(term)
        ))
      );
      setFilteredData(filtered);
    }
  };
  
  // Cargar datos
  useEffect(() => {
    setLoading(true);
    const baseURL = `${import.meta.env.VITE_IP}/getAll`;
    
    axios.post(baseURL + date)
      .then(response => {
        setData(response.data);
        setFilteredData(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error al cargar los datos:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los datos',
          confirmButtonColor: '#dc3545'
        });
        setLoading(false);
      });
  }, [date]);
  
  // Cargar datos de meses
  useEffect(() => {
    const baseURL = `${import.meta.env.VITE_IP}/getAll`;
    
    axios.post(baseURL + "month")
      .then(response => setMonths(response.data))
      .catch(error => console.error("Error al cargar los meses:", error));
  }, []);
  
  // Determinar el título según el tipo de vista
  const getTitle = () => {
    switch(date) {
      case "day": return "Reporte Diario";
      case "month": return "Reporte Mensual";
      case "year": return "Reporte Anual";
      default: return "Reporte";
    }
  };
  
  // Determinar la etiqueta de período según el tipo de vista
  const getPeriodLabel = () => {
    switch(date) {
      case "day": return "Día";
      case "month": return "Mes";
      case "year": return "Año";
      default: return "Período";
    }
  };
  
  return (
    <div className="stats-container p-4 bg-light">
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-primary bg-gradient text-white d-flex justify-content-between align-items-center">
              <h2 className="mb-0">{getTitle()}</h2>
              <button 
                className="btn btn-light d-flex align-items-center" 
                onClick={exportToExcel}
              >
                <i className="bi bi-file-earmark-excel me-2"></i>
                Exportar a Excel
              </button>
            </div>
            <div className="card-body">
              <div className="row g-4">
                <div className="col-md-3">
                  <div className="card border-primary h-100">
                    <div className="card-body text-center">
                      <h6 className="card-subtitle mb-2 text-muted">Ingresos Totales</h6>
                      <h3 className="card-title text-primary">${formatNumber(totales.ingresoTotal)}</h3>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-3">
                  <div className="card border-danger h-100">
                    <div className="card-body text-center">
                      <h6 className="card-subtitle mb-2 text-muted">Gastos Totales</h6>
                      <h3 className="card-title text-danger">${formatNumber(totales.gastoTotal)}</h3>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-3">
                  <div className="card border-success h-100">
                    <div className="card-body text-center">
                      <h6 className="card-subtitle mb-2 text-muted">Balance Neto</h6>
                      <h3 className={`card-title ${totales.balanceTotal >= 0 ? 'text-success' : 'text-danger'}`}>
                        ${formatNumber(totales.balanceTotal)}
                      </h3>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-3">
                  <div className="card border-info h-100">
                    <div className="card-body text-center">
                      <h6 className="card-subtitle mb-2 text-muted">Total Domicilios</h6>
                      <h3 className="card-title text-info">{formatNumber(totales.domiciliosTotal)}</h3>
                      {date === "day" && (
                        <p className="card-text">${formatNumber(totales.dineroDomiciliosTotal)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-primary bg-gradient text-white">
                  <h4 className="mb-0">Datos Principales</h4>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col">
                      <div className="input-group">
                        <span className="input-group-text" id="search-addon">
                          <i className="bi bi-search"></i>
                        </span>
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Buscar..." 
                          value={searchTerm} 
                          onChange={handleSearch}
                          aria-label="Buscar" 
                          aria-describedby="search-addon"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: '500px' }}>
                    <table className="table table-hover table-striped align-middle">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th scope="col" onClick={() => requestSort('data')} className="cursor-pointer">
                            {getPeriodLabel()} {sortConfig.key === 'data' && (
                              <i className={`bi bi-arrow-${sortConfig.direction === 'ascending' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          <th scope="col" onClick={() => requestSort('total')} className="cursor-pointer text-end">
                            Ingresos {sortConfig.key === 'total' && (
                              <i className={`bi bi-arrow-${sortConfig.direction === 'ascending' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          <th scope="col" onClick={() => requestSort('totalSalida')} className="cursor-pointer text-end">
                            Gastos {sortConfig.key === 'totalSalida' && (
                              <i className={`bi bi-arrow-${sortConfig.direction === 'ascending' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          <th scope="col" onClick={() => requestSort('balance')} className="cursor-pointer text-end">
                            Balance {sortConfig.key === 'balance' && (
                              <i className={`bi bi-arrow-${sortConfig.direction === 'ascending' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          <th scope="col" onClick={() => requestSort('numeroDomicilios')} className="cursor-pointer text-end">
                            Domicilios {sortConfig.key === 'numeroDomicilios' && (
                              <i className={`bi bi-arrow-${sortConfig.direction === 'ascending' ? 'up' : 'down'}`}></i>
                            )}
                          </th>
                          {date === "day" && (
                            <th scope="col" onClick={() => requestSort('dineroDomicilios')} className="cursor-pointer text-end">
                              $ Domicilios {sortConfig.key === 'dineroDomicilios' && (
                                <i className={`bi bi-arrow-${sortConfig.direction === 'ascending' ? 'up' : 'down'}`}></i>
                              )}
                            </th>
                          )}
                          <th scope="col">Detalle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((pedido) => {
                          if (pedido.ingresosData && typeof pedido.ingresosData === 'object') {
                            const balance = (pedido.total || 0) - (pedido.totalSalida || 0);
                            return (
                              <tr key={pedido._id}>
                                <th scope="row">
                                  {date === "year"
                                    ? pedido.data
                                    : pedido.data.charAt(0).toUpperCase() + pedido.data.slice(1)}
                                </th>
                                <td className="text-end">${formatNumber(pedido.total || 0)}</td>
                                <td className="text-end">${formatNumber(pedido.totalSalida || 0)}</td>
                                <td className={`text-end ${balance >= 0 ? 'text-success' : 'text-danger'}`}>
                                  ${formatNumber(balance)}
                                </td>
                                <td className="text-end">{pedido.numeroDomicilios || 0}</td>
                                {date === "day" && (
                                  <td className="text-end">${formatNumber(pedido.dineroDomicilios || 0)}</td>
                                )}
                                <td>
                                  <button 
                                    className="btn btn-sm btn-outline-primary" 
                                    type="button" 
                                    data-bs-toggle="collapse" 
                                    data-bs-target={`#collapse-${pedido._id}`} 
                                    aria-expanded="false" 
                                    aria-controls={`collapse-${pedido._id}`}
                                  >
                                    <i className="bi bi-info-circle me-1"></i>Ver detalle
                                  </button>
                                  <div className="collapse mt-2" id={`collapse-${pedido._id}`}>
                                    <div className="card card-body bg-light">
                                      <h6>Productos:</h6>
                                      <div className="row">
                                        {Object.entries(pedido.ingresosData.cantidadProducto || {}).map(([key, value]) => (
                                          <div key={key} className="col-md-6 mb-1">
                                            <span className="badge bg-primary me-1">{value}</span>
                                            <span>{key}</span>
                                            <span className="ms-2 text-success">
                                              ${formatNumber(pedido.ingresosData.dineroProducto?.[key] || 0)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          return null;
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-warning text-dark">
                  <h4 className="mb-0">Gastos</h4>
                </div>
                <div className="card-body">
                  <div className="table-responsive" style={{ maxHeight: '300px' }}>
                    <table className="table table-striped">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th scope="col">Concepto</th>
                          <th scope="col" className="text-end">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(gastosTotales).length === 0 ? (
                          <tr>
                            <td colSpan="2" className="text-center">No hay gastos registrados</td>
                          </tr>
                        ) : (
                          Object.keys(gastosTotales)
                            .sort((a, b) => gastosTotales[b] - gastosTotales[a])
                            .map((nombre) => (
                              <tr key={nombre}>
                                <td>{nombre}</td>
                                <td className="text-end">${formatNumber(gastosTotales[nombre])}</td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card-footer bg-light">
                  <div className="d-flex justify-content-between">
                    <h6 className="mb-0">Total gastos:</h6>
                    <h6 className="text-danger mb-0">${formatNumber(totales.gastoTotal)}</h6>
                  </div>
                </div>
              </div>
              
              <div className="card shadow-sm">
                <div className="card-header bg-secondary text-white">
                  <h4 className="mb-0">Agregar Gasto</h4>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label htmlFor="gastoNombre" className="form-label">Concepto</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        id="gastoNombre"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ej: Arriendo, Servicios, etc."
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="gastoPrecio" className="form-label">Monto</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input 
                          type="text" 
                          className="form-control" 
                          id="gastoPrecio"
                          value={price}
                          onChange={handlePriceChange}
                          placeholder="Ej: 50.000"
                          required
                        />
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary w-100">
                      <i className="bi bi-plus-circle me-2"></i>Agregar Gasto
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-info text-white">
              <h4 className="mb-0">Análisis de Datos</h4>
            </div>
            <div className="card-body">
              <CategoryCount data={date === "day" ? "Mes" : date === "month" ? "Año" : "year"} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StatsTable;